import { SmallDangerButton } from '../../../../components/buttons';
import { CaseQtyCell } from '../../../../components/product-table/columns/case-qty-cell';
import { ItemCodeCell } from '../../../../components/product-table/columns/item-code-cell';
import { MissingCaseQtyBadge } from '../../../../components/product-table/columns/missing-case-qty-badge';
import { SalesDataPopover } from '../../../../components/product-table/sales-data-popover';
import { casesOf } from '../../../../data/order-item';
import { casesSold, inventoryCases, saleStat, tierPrice } from '../../../../data/product';
import {
  fullCurrencyFormatter,
  preciseCurrencyFormatter,
} from '../../../../helpers/format-currency';
import { formatCases, formatDoh } from '../../../../helpers/format-quantity';
import type { Family } from '../data/family';
import type { ProductOrderItem } from '../data/product-order-item';
import { useIsRowGlowing } from '../hooks/pivot-glow-store';
import { navigateNamedInputs } from '../utils/navigate-named-inputs';
import { CountCell } from './count-cell';
import { MutedBadge, mutedReason } from './muted-badge';
import {
  DEAL_COLUMN_PX,
  DealCellGrid,
  FourCellGrid,
  VolumeDiscountCell,
} from './volume-discount-column';

const FAMILY_CASES_INPUT_NAME = 'family-cases-input';

type Props = {
  family: Family;
  /** Total cases across the family — drives the tier every row is billed at. */
  familyCases: number;
  /** Deal column count, lifted to the view so cards share the same layout. */
  tierSlotCount: number;
  setOrderItem: (item: ProductOrderItem) => void;
  onRemove: (item: ProductOrderItem) => void;
};

/**
 * Fat-row SKU table inside a family card. Columns:
 *  item code | description | size | bpc | current cost |
 *  Deal 1..N (dynamic) | 30/60/90/180D sales | on-hand (stack) | cases input |
 *  extended | × Remove
 */
export function FamilySkuTable({
  family,
  familyCases,
  tierSlotCount,
  setOrderItem,
  onRemove,
}: Props) {
  const tierSlots = Array.from({ length: tierSlotCount }, (_, i) => i + 1);

  return (
    <table className="w-full table-fixed border-separate border-spacing-0 text-sm tabular-nums [&_td]:align-top [&_th]:align-bottom">
      {/* Description is the only flexible column — table-fixed + w-full sends
          all leftover card width into it. Deal columns must stay at exactly
          DEAL_COLUMN_PX so the next-month popover skid math in
          volume-discount-column.tsx lines up. */}
      <colgroup>
        <col className="w-[72px]" />
        <col />
        <col className="w-[88px]" />
        <col className="w-[56px]" />
        <col style={{ width: DEAL_COLUMN_PX }} />
        {tierSlots.map((slot) => (
          <col key={slot} style={{ width: DEAL_COLUMN_PX }} />
        ))}
        <col className="w-[80px]" />
        <col className="w-[80px]" />
        <col className="w-[80px]" />
        <col className="w-[80px]" />
        <col className="w-[80px]" />
        <col className="w-[88px]" />
        <col className="w-[96px]" />
        <col className="w-[106px]" />
      </colgroup>
      <thead>
        <tr className="text-xs uppercase text-gray-500">
          <th className="px-2 py-1 text-left">Item Code</th>
          <th className="px-2 py-1 text-left">Description</th>
          <th className="px-2 py-1 text-right">Size</th>
          <th className="px-2 py-1 text-right">Case Qty</th>
          <th className="px-1 py-1 text-right">Current Cost</th>
          {tierSlots.map((slot) => (
            <th key={slot} className="px-1 py-1 text-right">
              Deal {slot}
            </th>
          ))}
          <th className="px-2 py-1 text-right">30D Sales</th>
          <th className="px-2 py-1 text-right">60D Sales</th>
          <th className="px-2 py-1 text-right">90D Sales</th>
          <th className="px-2 py-1 text-right">180D Sales</th>
          <th className="px-2 py-1 text-right">On hand</th>
          <th className="px-2 py-1 text-right">Cases</th>
          <th className="px-2 py-1 text-right">Extended</th>
          <th className="px-2 py-1 text-right" />
        </tr>
      </thead>
      <tbody>
        {family.members.map((item) => (
          <MemberRow
            key={item.product.id}
            item={item}
            familyCases={familyCases}
            tierSlots={tierSlots}
            setOrderItem={setOrderItem}
            onRemove={onRemove}
            canRemove={family.members.length > 1}
          />
        ))}
      </tbody>
    </table>
  );
}

function MemberRow({
  item,
  familyCases,
  tierSlots,
  setOrderItem,
  onRemove,
  canRemove,
}: {
  item: ProductOrderItem;
  familyCases: number;
  tierSlots: number[];
  setOrderItem: (item: ProductOrderItem) => void;
  onRemove: (item: ProductOrderItem) => void;
  canRemove: boolean;
}) {
  const { product } = item;
  const isGlowing = useIsRowGlowing(product.id);
  const casesVal = casesOf(item);
  const extended = casesVal * tierPrice(product, familyCases);
  const cs30d = casesSold(product, 30);
  const cs60d = casesSold(product, 60);
  const cs90d = casesSold(product, 90);
  const cs180d = casesSold(product, 180);

  const reason = mutedReason(product);

  // Dim inactive rows so they stand out as "exceptional" — same opacity as the
  // regular order view's no-count rows. Opacity goes on each non-action cell
  // (not the row) so the × Remove button stays full contrast — `opacity` is
  // multiplicative, so a child can't undo a parent. DO NOT disable the cases
  // input either: store owners may want to override (e.g. seed a SKU they're
  // re-introducing, or order a deal on a temporarily-disabled item).
  const dim = reason ? 'opacity-60' : '';

  return (
    <tr className={isGlowing ? 'animate-pivot-glow' : ''}>
      <td className={`px-2 py-1 ${dim}`}>
        <ItemCodeCell product={product} />
      </td>
      <td className={`truncate px-2 py-1 ${dim}`}>{product.description}</td>
      <td className={`px-2 py-1 text-right ${dim}`}>
        <div>{product.size}</div>
        {reason && <MutedBadge reason={reason} />}
      </td>
      <td className={`px-2 py-1 text-right ${dim}`}>
        <CaseQtyCell product={product} />
      </td>
      <td className={`px-1 py-1 ${dim}`}>
        <DealCellGrid
          cost={
            product.pricing.current_cost != null
              ? preciseCurrencyFormatter(product.pricing.current_cost)
              : '—'
          }
        />
      </td>
      {tierSlots.map((slot) => (
        <td key={slot} className={`px-1 py-1 ${dim}`}>
          <VolumeDiscountCell orderItem={item} slot={slot} />
        </td>
      ))}
      <td className={`px-2 py-1 text-right ${dim}`}>
        <SalesDataPopover product={product} isCasesView salesStat={30}>
          <span className="border-b border-dotted border-gray-400">{formatCases(cs30d)}</span>
        </SalesDataPopover>
      </td>
      <td className={`px-2 py-1 text-right ${dim}`}>
        <SalesDataPopover product={product} isCasesView salesStat={60}>
          <span className="border-b border-dotted border-gray-400">{formatCases(cs60d)}</span>
        </SalesDataPopover>
      </td>
      <td className={`px-2 py-1 text-right ${dim}`}>
        <SalesDataPopover product={product} isCasesView salesStat={90}>
          <span className="border-b border-dotted border-gray-400">{formatCases(cs90d)}</span>
        </SalesDataPopover>
      </td>
      <td className={`px-2 py-1 text-right ${dim}`}>
        <SalesDataPopover product={product} isCasesView salesStat={180}>
          <span className="border-b border-dotted border-gray-400">{formatCases(cs180d)}</span>
        </SalesDataPopover>
      </td>
      <td className={`px-2 py-1 text-right ${dim}`} data-testid="on-hand-cell">
        <OnHandCell item={item} />
      </td>
      <td className={`px-2 py-1 text-right ${dim}`}>
        <CountCell
          orderItem={item}
          getCount={casesOf}
          setCount={(orderItem, count) => setOrderItem({ ...orderItem, cases: count, units: 0 })}
          name={FAMILY_CASES_INPUT_NAME}
          onKeyDown={(e) => navigateNamedInputs(e, FAMILY_CASES_INPUT_NAME)}
          onFocus={(e) => e.currentTarget.select()}
        />
      </td>
      <td className={`px-2 py-1 text-right ${dim}`}>{fullCurrencyFormatter(extended)}</td>
      <td className="px-2 py-1 text-right">
        {canRemove && (
          <SmallDangerButton onClick={() => onRemove(item)}>× Remove</SmallDangerButton>
        )}
      </td>
    </tr>
  );
}

function OnHandCell({ item }: { item: ProductOrderItem }) {
  const { product } = item;
  const onHandUnits = product.inventory_qty;
  const sales90d = saleStat(product, 90);
  const doh = sales90d > 0 ? product.stats.days_on_hand : null;
  const out = onHandUnits <= 0;

  // Cases line: when case_qty is unknown, inventoryCases() falls back to /1
  // and would render units-as-cases. Show the missing-data badge instead so
  // the row reads consistently with the Case Qty column on the same row.
  const caseQtyKnown = product.case_qty > 0;
  const onHandCases = caseQtyKnown ? inventoryCases(product) : 0;
  const low = caseQtyKnown && onHandCases > 0 && onHandCases < casesSold(product, 30);
  const casesClass = out ? 'text-red-600' : low ? 'text-orange-600' : '';

  const casesNode = caseQtyKnown ? (
    <span className={casesClass}>{formatCases(onHandCases)}</span>
  ) : (
    <MissingCaseQtyBadge tooltip="Missing case quantity. We can't tell how many cases this is — update the case quantity in the product catalog to fix." />
  );

  return (
    <FourCellGrid
      row1={casesNode}
      row2={<span className="text-gray-500">{onHandUnits}U</span>}
      row3={
        doh === null ? (
          <span className="text-gray-400">—</span>
        ) : out ? (
          <span className="font-bold text-red-600">OUT</span>
        ) : (
          <span className="text-gray-500">{formatDoh(doh)}d</span>
        )
      }
    />
  );
}
